import { Enums as csToolsEnums } from '@cornerstonejs/tools';
import { ServicesManager } from '@ohif/core';

// Segments are found again by this key, stored on the segment itself so it
// lives and dies with the segmentation state (reset on mode exit).
const SEGMENT_KEY_STAT = 'gleamerSegmentKey';

// `ensureMaskSegment` runs from several effects and commands at once; the
// labelmap creation is async, so concurrent calls for one segmentation must
// share it instead of creating it twice.
const pendingCreations = new Map<string, Promise<string>>();

export function hexToRgba(
  hex: string
): [number, number, number, number] | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) {
    return null;
  }
  const digits =
    match[1].length === 3
      ? match[1]
          .split('')
          .map(digit => digit + digit)
          .join('')
      : match[1];
  return [
    parseInt(digits.slice(0, 2), 16),
    parseInt(digits.slice(2, 4), 16),
    parseInt(digits.slice(4, 6), 16),
    255,
  ];
}

/** One labelmap segmentation per display set. */
export function getMaskSegmentationId(displaySetInstanceUID: string): string {
  return `gleamer-mask-${displaySetInstanceUID}`;
}

type EnsureMaskSegmentArgs = {
  viewportId: string;
  segmentKey: string;
  label: string;
  color?: string;
  // Aborted when the caller wants another Segment; the stale call then stops
  // before it selects its own.
  signal?: AbortSignal;
};

/**
 * Makes sure the viewport's display set has its Mask labelmap, that the
 * viewport shows it, and that the Segment for `segmentKey` exists and is the
 * one the Brush paints in.
 *
 * Returns false when the viewport shows no display set, or when `signal` was
 * aborted while the labelmap was being prepared.
 */
export async function ensureMaskSegment(
  servicesManager: ServicesManager,
  { viewportId, segmentKey, label, color, signal }: EnsureMaskSegmentArgs
): Promise<boolean> {
  const { viewportGridService, displaySetService, segmentationService } =
    servicesManager.services;

  const [displaySetInstanceUID] =
    viewportGridService.getDisplaySetsUIDsForViewport(viewportId) ?? [];
  const displaySet =
    displaySetInstanceUID &&
    displaySetService.getDisplaySetByUID(displaySetInstanceUID);
  if (!displaySet) {
    return false;
  }

  const segmentationId = getMaskSegmentationId(displaySetInstanceUID);
  const segmentStats = { [SEGMENT_KEY_STAT]: segmentKey };

  if (!segmentationService.getSegmentation(segmentationId)) {
    let pending = pendingCreations.get(segmentationId);
    if (!pending) {
      // The first Segment is created with the labelmap; otherwise OHIF seeds
      // a "Segment 1" that belongs to no Observation.
      pending = segmentationService
        .createLabelmapForDisplaySet(displaySet, {
          segmentationId,
          label: 'Mask',
          segments: { 1: { label, cachedStats: segmentStats, active: true } },
        })
        .finally(() => pendingCreations.delete(segmentationId));
      pendingCreations.set(segmentationId, pending);
    }
    await pending;
  }

  const hasRepresentation =
    segmentationService.getSegmentationRepresentations(viewportId, {
      segmentationId,
    }).length > 0;
  if (!hasRepresentation) {
    await segmentationService.addSegmentationRepresentation(viewportId, {
      segmentationId,
      type: csToolsEnums.SegmentationRepresentations.Labelmap,
    });
  }
  if (signal?.aborted) {
    return false;
  }
  segmentationService.setActiveSegmentation(viewportId, segmentationId);

  const rgba = color ? hexToRgba(color) : null;
  const segmentation = segmentationService.getSegmentation(segmentationId);
  const existing = Object.values(segmentation?.segments ?? {}).find(
    segment => segment?.cachedStats?.[SEGMENT_KEY_STAT] === segmentKey
  );

  if (!existing) {
    // `addSegment` makes the new segment active and colors it in every
    // viewport that shows the segmentation.
    segmentationService.addSegment(segmentationId, {
      label,
      cachedStats: segmentStats,
      ...(rgba && { color: rgba }),
    });
    return true;
  }

  segmentationService.setActiveSegment(segmentationId, existing.segmentIndex);
  if (rgba) {
    segmentationService.setSegmentColor(
      viewportId,
      segmentationId,
      existing.segmentIndex,
      rgba
    );
  }
  return true;
}
